import React, { useState, useEffect } from 'react';
import './TweetBotz.css';

const TWEET_API = process.env.REACT_APP_API_URL || 'https://api.cinebotz.com';

function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const now = new Date();
  const sec = Math.floor((now - date) / 1000);
  if (sec < 60) return 'Just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  if (sec < 604800) return `${Math.floor(sec / 86400)}d`;
  return date.toLocaleDateString();
}

function decodeHtml(text) {
  if (!text) return '';
  const el = document.createElement('textarea');
  el.innerHTML = text;
  return el.value;
}

function formatCount(n) {
  const num = Number(n) || 0;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(num);
}

function normalizeTweet(raw) {
  const author = raw.author || {};
  const media = raw.media || null;
  const metrics = raw.metrics || {};
  return {
    tweetId: raw.tweetId || raw.id || '',
    tweetUrl: raw.tweetUrl || raw.url || '#',
    text: decodeHtml(raw.text || ''),
    createdAt: raw.createdAt || raw.date || '',
    author: {
      name: author.name || author.displayName || 'Unknown',
      username: author.username || author.screenName || '',
      profileImageUrl: author.profileImageUrl || author.avatar || '',
      verified: Boolean(author.verified),
    },
    media: media
      ? {
          url: media.url || media.previewImageUrl || '',
          previewImageUrl: media.previewImageUrl || media.url || '',
          mediaType: media.mediaType || 'photo',
        }
      : null,
    metrics: {
      likeCount: metrics.likeCount ?? metrics.likes ?? 0,
      retweetCount: metrics.retweetCount ?? metrics.retweets ?? 0,
      replyCount: metrics.replyCount ?? metrics.replies ?? 0,
      quoteCount: metrics.quoteCount ?? metrics.quotes ?? 0,
    },
  };
}

export function TweetBotz() {
  const [tweets, setTweets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const url = TWEET_API ? `${TWEET_API}/api/twitterv2` : '/api/twitterv2';
    fetch(url)
      .then((res) => {
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          return res.text().then(() => {
            throw new Error(
              res.ok
                ? 'Server did not return JSON.'
                : `Tweet API error (${res.status}). Make sure the backend is running.`
            );
          });
        }
        if (!res.ok) {
          throw new Error(`Tweet API error: ${res.status} ${res.statusText}`);
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data.tweets || data.items || []);
        setTweets(list.slice(0, 5).map(normalizeTweet));
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Could not load tweets.');
          setTweets([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="tweet-botz tweet-botz--loading">
        <div className="tweet-botz-spinner" aria-hidden />
        <p className="tweet-botz-loading-text">Loading Tweet Botz…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tweet-botz tweet-botz--error">
        <p className="tweet-botz-error-text">{error}</p>
      </div>
    );
  }

  return (
    <div className="tweet-botz">
      <h1 className="tweet-botz-title">Tweet Botz</h1>
      <p className="tweet-botz-subtitle">Top Tamil cinema tweets</p>
      <ul className="tweet-botz-list">
        {tweets.map((tweet) => (
          <li key={tweet.tweetId || tweet.tweetUrl} className="tweet-botz-card">
            <a
              href={tweet.tweetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="tweet-botz-card-link"
            >
              <div className="tweet-botz-card-header">
                {tweet.author.profileImageUrl && (
                  <img
                    src={tweet.author.profileImageUrl}
                    alt=""
                    className="tweet-botz-avatar"
                  />
                )}
                <div className="tweet-botz-author">
                  <span className="tweet-botz-author-name">
                    {tweet.author.name}
                    {tweet.author.verified && (
                      <span className="tweet-botz-verified" aria-label="Verified">✓</span>
                    )}
                  </span>
                  {tweet.author.username && (
                    <span className="tweet-botz-username">@{tweet.author.username}</span>
                  )}
                </div>
                <time className="tweet-botz-time" dateTime={tweet.createdAt}>
                  {formatTimeAgo(tweet.createdAt)}
                </time>
              </div>

              <p className="tweet-botz-text">{tweet.text}</p>

              {tweet.media?.url && (
                <div className="tweet-botz-media">
                  <img
                    src={tweet.media.previewImageUrl || tweet.media.url}
                    alt=""
                    className="tweet-botz-media-img"
                    loading="lazy"
                  />
                </div>
              )}

              <div className="tweet-botz-metrics" aria-label="Engagement">
                {tweet.metrics.replyCount > 0 && (
                  <span className="tweet-botz-metric">
                    {formatCount(tweet.metrics.replyCount)} replies
                  </span>
                )}
                {tweet.metrics.retweetCount > 0 && (
                  <span className="tweet-botz-metric">
                    {formatCount(tweet.metrics.retweetCount)} reposts
                  </span>
                )}
                <span className="tweet-botz-metric tweet-botz-metric--likes">
                  {formatCount(tweet.metrics.likeCount)} likes
                </span>
              </div>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default TweetBotz;
